import type {
  ChangeEvent,
  FC,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
} from 'react';
import cn from '@/ui/utils/cn/cn';
import VectorGraphic, { type IconName } from '../VectorGraphic/VectorGraphic';

import $ from './Input.module.scss';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'search' | 'date';
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  testid?: string;
  fullWidth?: boolean;
  name?: string;
  ariaLabel?: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  valid?: boolean;
  errorMessage?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  icon?: IconName;
  iconClassName?: string;
  handleIconClick?: () => void;
  iconPosition?: 'default' | 'special';
  iconAriaLabel?: string;
};

const InputIcon: FC<{
  icon?: IconName;
  handleIconClick?: () => void;
  iconClassName?: string;
  iconPosition?: 'default' | 'special';
  iconAriaLabel?: string;
}> = ({
  icon,
  handleIconClick,
  iconClassName,
  iconPosition,
  iconAriaLabel,
}) => {
  const classNames = cn(
    $.iconWrapper,
    iconPosition === 'default' && $.isDefaultPosition,
    iconPosition === 'special' && $.isSpecialPosition,
  );
  if (icon && handleIconClick) {
    return (
      <button
        type="button"
        className={classNames}
        onClick={handleIconClick}
        aria-label={iconAriaLabel}
      >
        <VectorGraphic name={icon} className={cn($.icon, iconClassName)} />
      </button>
    );
  }

  if (icon) {
    return (
      <div className={classNames} aria-label={iconAriaLabel}>
        <VectorGraphic name={icon} className={cn($.icon, iconClassName)} />
      </div>
    );
  }

  return null;
};

const Input: FC<InputProps> = ({
  type = 'text',
  value,
  onChange,
  placeholder = '',
  disabled,
  className = '',
  testid,
  fullWidth,
  name,
  ariaLabel,
  onBlur,
  onFocus,
  valid,
  errorMessage,
  onKeyDown,
  icon,
  iconClassName,
  handleIconClick,
  iconPosition = 'default',
  iconAriaLabel,
  ...rest
}) => {
  const isError = valid === false || errorMessage;
  const classNames = cn(
    $.input,
    fullWidth && $.fullWidth,
    isError && $.errorInput,
    className,
  );

  return (
    <div className={$.container}>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={classNames}
        name={name}
        aria-label={ariaLabel}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        {...rest}
      />

      <InputIcon
        icon={icon}
        handleIconClick={handleIconClick}
        iconClassName={iconClassName}
        iconPosition={iconPosition}
        iconAriaLabel={iconAriaLabel}
      />
    </div>
  );
};

export default Input;
